import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import BotanicalArt from "@/components/BotanicalArt";

const FAQS = [
  {
    q: "What actually happens in the first session?",
    a: "Mostly, your therapist listens. They'll ask a few gentle questions about what brought you here and what you'd like to be different. There's no test, no diagnosis on day one, and nothing you have to share before you're ready. Most people say it felt like a weight off — just saying things out loud.",
  },
  {
    q: "What if I don't know what to say?",
    a: "That's more common than you'd think, and therapists are trained for exactly this. You won't sit in awkward silence — your therapist will guide the conversation with questions. Starting with \"I don't really know where to start\" is a perfectly good start.",
  },
  {
    q: "Will my therapist judge me?",
    a: "No. Therapists are trained to listen without judgment, and at Eunoia it's the first thing we look for in who we bring on. Whatever you bring — anger, shame, something you've never told anyone — will be met with empathy, not shock or moralising.",
  },
  {
    q: "How long is a session, and how often should I attend?",
    a: "Sessions run 30 to 60 minutes, depending on your therapist. Most people begin with one session a week, then adjust together with their therapist — some move to every two weeks, some check in monthly. There's no minimum commitment; you go at your own pace.",
  },
  {
    q: "How do I know which therapist is right for me?",
    a: "Start with what you want support with and the language you're most comfortable in — our matching flow narrows it down. Then read their profiles and notice who you feel drawn to. That instinct matters more than any resume.",
  },
  {
    q: "Can I change therapists?",
    a: "Anytime, free, with no awkward conversation required. The relationship between you and your therapist is the biggest factor in therapy working — we'd rather you find the right fit than stick with the wrong one.",
  },
  {
    q: "Is online therapy actually private?",
    a: "Yes. Sessions happen over secure video or audio, and we don't store your chats or keep session histories. No records, no paper trail — nothing appears on shared family accounts, insurance, or anywhere else. What you say stays between you and your psychologist.",
  },
  {
    q: "How much does it cost?",
    a: "Your first session is ₹99 — the same for every therapist. No fake discounts, no packages, no hidden fees. You book first and pay via a secure link when you're ready.",
  },
  {
    q: "Is my problem 'serious enough' for therapy?",
    a: "If it's taking up space in your head, it's serious enough. You don't need a diagnosis or a crisis to talk to someone. Many people come simply because they're tired of carrying something alone — that's reason enough.",
  },
  {
    q: "What if therapy doesn't feel right for me?",
    a: "Then you can stop, anytime, with nothing owed beyond the sessions you've had. Therapy isn't for everyone at every moment — but most people find one honest conversation is enough to know whether it helps.",
  },
];

export default function Faqs() {
  return (
    <div className="mx-auto max-w-screen-lg px-6 py-16 md:py-24" data-testid="faqs-page">
      <Reveal>
        <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-accent">
          <span className="h-px w-10 bg-accent" /> Questions, answered honestly
        </p>
        <h1 className="mt-6 font-serif text-4xl font-bold leading-tight tracking-tight md:text-6xl">
          Questions, answered.
        </h1>
      </Reveal>

      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-14" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-border">
              <AccordionTrigger
                className="py-6 text-left font-serif text-xl font-bold tracking-tight transition-colors hover:text-accent hover:no-underline md:text-2xl"
                data-testid={`faq-q-${i}`}
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-7 pr-6 text-sm leading-relaxed text-muted-foreground md:text-base" data-testid={`faq-a-${i}`}>
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-16 flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-primary p-10 text-primary-foreground" data-testid="faq-cta-band">
          <p className="max-w-md font-serif text-2xl font-bold tracking-tight md:text-3xl">
            Still unsure? Ask us directly — a real person replies.
          </p>
          <Link
            to="/contact"
            data-testid="faq-cta-contact"
            className="group flex items-center gap-2 rounded-full bg-primary-foreground px-7 py-4 text-sm font-bold text-primary transition-transform duration-300 hover:scale-[1.04] active:scale-95"
          >
            Talk to Eunoia
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <BotanicalArt variant="seed" dark className="w-full max-w-[13rem]" />
        </div>
      </Reveal>
    </div>
  );
}
